import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ShieldCheck, Mail, RefreshCw, Users, AlertCircle } from 'lucide-react';

const MfaAdminPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API}/admin/mfa/directory`);
      setData(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load the MFA register');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return <div className="text-center py-8">Loading MFA register...</div>;
  }
  if (!data) {
    return <div className="text-center py-8 text-red-600">The MFA register could not be loaded.</div>;
  }

  const s = data.summary;
  const tiles = [
    { label: 'Accounts', value: s.total },
    { label: 'MFA enforced at sign-in', value: s.enforced },
    { label: 'Enrolment confirmed', value: s.enrolment_confirmed },
    { label: 'Pending first sign-in', value: s.pending_first_signin },
    { label: 'Shared logins', value: s.shared_logins }
  ];

  return (
    <div className="space-y-6">
      <Card className="border-2 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Multi-Factor Authentication register
          </CardTitle>
          <CardDescription>
            Every account, where its one-time sign-in code is delivered, and who authenticates each shared login.
            This is an information-only view. The MFA settings themselves (the box above, for super admins) are changed by a super admin.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {tiles.map((t) => (
              <div key={t.label} className="p-4 bg-white rounded-lg border text-center">
                <div className="text-2xl font-bold text-blue-600">{t.value}</div>
                <div className="text-xs text-gray-500">{t.label}</div>
              </div>
            ))}
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-900 space-y-1">
            <div>
              <strong>MFA requirement:</strong>{' '}
              {data.mfa_required_globally ? 'ON. Every sign-in needs a code.' : 'OFF. Codes are not being required.'}
            </div>
            <div>
              <strong>Method:</strong>{' '}
              {data.authenticator_enabled ? 'Email code or authenticator app.' : 'Email code only. The authenticator app is switched off for every user.'}
            </div>
            <div>
              <strong>How it works:</strong> a password alone never gives access. The account receives a six-digit code at the address below,
              valid for 10 minutes and usable once. An account with no valid address cannot sign in at all.
            </div>
            <div>
              <strong>Enrolment:</strong> "Pending first sign-in" means the account has not signed in since MFA became mandatory.
              It is still fully enforced: that account cannot reach the system without completing the emailed code.
            </div>
          </div>

          {s.without_valid_email > 0 && (
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-300 text-sm text-yellow-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {s.without_valid_email} account(s) have no valid address for codes and cannot sign in until one is set.
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="mfa-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Accounts
          </CardTitle>
          <CardDescription>
            Shared logins are listed first. Each names the one individual who receives its codes and authenticates it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mfa-register-table">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                  <th className="py-2 pr-3">Account</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">MFA address</th>
                  <th className="py-2 pr-3">Enrolment</th>
                  <th className="py-2">Trusted devices</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.id} className="border-b align-top">
                    <td className="py-3 pr-3">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-xs text-gray-500">{u.username}</div>
                      {!u.approved && <span className="text-xs text-red-600">Not approved</span>}
                    </td>
                    <td className="py-3 pr-3">
                      {u.account_type === 'shared' ? (
                        <div>
                          <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">Shared login</span>
                          <div className="text-xs text-gray-600 mt-1">
                            Authenticated by: {u.accountable_missing ? <span className="text-red-600">person no longer exists</span> : <strong>{u.accountable_name}</strong>}
                          </div>
                        </div>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">Individual</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      {!u.delivery_email_valid ? (
                        <span className="text-red-600">No valid address (cannot sign in)</span>
                      ) : u.delivery_email_is_custom ? (
                        <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3 text-green-600" />{u.delivery_email}</span>
                      ) : (
                        <span className="text-gray-400">Same as login</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      {u.enrolment === 'confirmed' ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Confirmed</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Pending first sign-in</span>
                      )}
                      <div className="text-xs text-gray-500 mt-1">{u.enforced ? 'Enforced at every sign-in' : 'Not enforced'}</div>
                    </td>
                    <td className="py-3">{u.trusted_device_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent MFA setting changes</CardTitle>
          <CardDescription>The last 10 unlocks, setting changes and shared-login changes.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.recent_changes.length === 0 ? (
            <p className="text-sm text-gray-500">No changes recorded yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.recent_changes.map((c, i) => (
                <li key={`${c.at}-${i}`} className="flex flex-wrap gap-x-3 border-b pb-2">
                  <span className="text-gray-500">{new Date(c.at).toLocaleString()}</span>
                  <span className="font-medium">{c.action.replace(/_/g, ' ')}</span>
                  <span className="text-gray-600">{c.detail}</span>
                  <span className="text-gray-400">by {c.actor_email}</span>
                  {!c.success && <span className="text-red-600">refused</span>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MfaAdminPage;
